import React, { useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
    IconButton,
    InputAdornment,
    Stack,
    TextField,
} from "@mui/material";

const getThousandSeparator = (locale) =>
    new Intl.NumberFormat(locale)
        .formatToParts(1234.5678)
        .find((part) => part.type === "group")?.value || "";

const getDecimalSeparator = (locale) =>
    new Intl.NumberFormat(locale)
        .formatToParts(1234.5678)
        .find((part) => part.type === "decimal")?.value || ".";

const NumericFormatCustom = React.forwardRef((props, ref) => {
    const {
        decimalSeparator,
        thousandSeparator,
        value,
        locale = "en-US",
        ...rest
    } = props;
    
    const defaultThousandSeparator = useMemo(
        () => getThousandSeparator(locale),
        [locale]
    );

    const defaultDecimalSeparator = useMemo(
        () => getDecimalSeparator(locale),
        [locale]
    );
    
    return (
        <NumericFormat
            {...rest}
            value={value ?? ""} 
            thousandSeparator={thousandSeparator || defaultThousandSeparator}
            decimalSeparator={decimalSeparator || defaultDecimalSeparator}
            getInputRef={ref} 
        />
    );
});

const NumberInput = ({
                         disabled = false,
                         hideActionButtons = false,
                         max = Infinity,
                         min = -Infinity,
                         numericFormatProps: numericFormatPropsProp,
                         onChange,
                         size,
                         slotProps,
                         label,
                         step = 1,
                         value: valueProp,
                         ...rest
                     }) => {
    const t = (key) => key; 

    const isControlled = valueProp !== undefined && onChange !== undefined;
    const [fallbackValue, setFallbackValue] = useState(valueProp);
    const value = isControlled ? valueProp : fallbackValue;
    const setValue = isControlled ? onChange : setFallbackValue;

    const increment = () => {
        const newValue = (value != null && !isNaN(value) ? value : 0) + step;
        if (newValue <= max) setValue(newValue);
    };

    const decrement = () => {
        const newValue = (value != null && !isNaN(value) ? value : 0) - step;
        if (newValue >= min) setValue(newValue);
    };

    const numericFormatProps = {
        decimalScale: 5, // Default decimal scale
        isAllowed: (values) =>
            values.floatValue == null ||
            (values.floatValue >= min && values.floatValue <= max),
        max: max !== Infinity ? max : undefined,
        min: min !== -Infinity ? min : undefined,
        step: step !== 1 ? step : undefined,
        onKeyDown: (event) => {
            if (event.key === "ArrowUp") increment();
            else if (event.key === "ArrowDown") decrement();
        },
        onValueChange: (values) => {
            const floatValue = values.floatValue;
            if (floatValue !== value) setValue(floatValue ?? null); 
        },
        value,
        ...numericFormatPropsProp, 
    };

    // Common props for icon buttons
    const commonAdornmentButtonProps = {
        edge: "end",
        sx: { p: size !== "small" ? "1px" : 0 },
    };

    // Render `NumberInput`
    return (
        <TextField
            {...rest}
            value={value ?? ""}
            disabled={disabled}
            label={label}
            size={size}
            sx={{
                width: { xs: "160px !important" }, 
            }}
            slotProps={{
                ...slotProps,
                input: {
                    inputComponent: NumericFormatCustom,
                    endAdornment: !hideActionButtons ? (
                        <InputAdornment position="end">
                            <Stack>
                                <IconButton
                                    aria-label={t("incrementAriaLabel")}
                                    disabled={disabled || (value || 0) + step > max}
                                    onClick={increment}
                                    {...commonAdornmentButtonProps}
                                >
                                    <KeyboardArrowUpIcon fontSize={size} />
                                </IconButton>
                                <IconButton
                                    aria-label={t("decrementAriaLabel")}
                                    disabled={disabled || (value || 0) - step < min}
                                    onClick={decrement}
                                    {...commonAdornmentButtonProps}
                                >
                                    <KeyboardArrowDownIcon fontSize={size} />
                                </IconButton>
                            </Stack>
                        </InputAdornment>
                    ) : undefined,
                    ...slotProps?.input,
                },
                htmlInput: {
                    ...slotProps?.htmlInput,
                    ...numericFormatProps,
                },
            }}
        />
    );
};

export default NumberInput;